import { useEffect, useState } from "react"
import { Button, Typography } from "@mui/material"
import { PostBotPageLayout } from "../components/common/postBotPageLayout"
import { skipToken } from "@reduxjs/toolkit/query"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { v4 as uuid, validate as isUuid } from "uuid"
import type { TemplateWriteAccess } from "../hooks/useVersionDraft"
import {
  publicationPath,
  publicationsPath,
  readPublicationDraft,
} from "../hooks/publicationSessionDraft"
import { useGetPublicationQuery } from "../services/publications/publicationsApi"
import { PostBotNavigation } from "../components/common/postBotNavigation"
import { TemplateQueryFeedback } from "../components/templates/templateQueryFeedback"
import { PublicationForm } from "../components/publications/publicationForm"
import type { Publication } from "../interface/publications"

export interface PublicationPageProps extends TemplateWriteAccess {
  publicationId?: string
}
export default function PublicationPage(props: PublicationPageProps) {
  const params = useParams()
  const id = props.publicationId ?? params.publicationId ?? params.id ?? "new"
  return <PublicationDetail key={id} {...props} publicationId={id} />
}
function PublicationDetail({
  publicationId: id,
  ...access
}: PublicationPageProps & { publicationId: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [draftId] = useState(() => {
    if (id !== "new") return id
    const retainedId = location.state?.publicationDraftId
    return typeof retainedId === "string" && isUuid(retainedId)
      ? retainedId
      : uuid()
  })
  useEffect(() => {
    if (id !== "new" || location.state?.publicationDraftId === draftId) return
    // Keep only the identity in the original entry. Back must find the same
    // memory-only draft; replacing (not pushing) must not add a history step.
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: { ...location.state, publicationDraftId: draftId },
      },
    )
  }, [id, draftId, location, navigate])
  const [retained] = useState(() => readPublicationDraft(draftId))
  const creating = id === "new" || retained?.baseline === null
  const query = useGetPublicationQuery(creating ? skipToken : id)
  const detail =
    query.currentData?.id === id && !query.error ? query.currentData : undefined
  const [initial, setInitial] = useState<Publication | undefined>(
    retained?.baseline ?? undefined,
  )
  // Latch the first confirmed identity; a refetch/error must not unmount a dirty form.
  if (!initial && detail && !creating) setInitial(detail)
  return (
    <PostBotPageLayout>
      <PostBotNavigation />
      <Button
        component={Link}
        to={publicationsPath}
        sx={{ alignSelf: "flex-start" }}
      >
        Библиотека публикаций
      </Button>
      <Typography variant="h5">
        {creating ? "Новая публикация" : "Публикация"}
      </Typography>
      {!creating && (
        <TemplateQueryFeedback
          query={query}
          empty={!initial && !detail && "Публикация не найдена"}
        />
      )}
      {(creating || initial) && (
        <PublicationForm
          {...access}
          initial={initial}
          draftId={draftId}
          onSaved={(saved) => {
            if (creating)
              navigate(publicationPath(saved.id), {
                replace: true,
                state: null,
              })
          }}
        />
      )}
    </PostBotPageLayout>
  )
}
